import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, deleteDoc, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { 
  Bell, BellOff, Check, Trash2, Clock, 
  MessageSquare, Zap, AlertTriangle, CheckCircle2, 
  Loader2, ShieldAlert, Timer, Megaphone, Calendar, Info, Ban, Star, Gavel
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/ui/Navbar';
import { toast } from 'sonner';

import planoFundo from "../assets/planodefundo.png"; 

const Notification = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); 

  const isTechUser = userProfile?.userType?.toLowerCase() === 'technician' || userProfile?.userType?.toLowerCase() === 'admin';

useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setNotifications(data);
        setLoading(false); // Sucesso: desliga o carregamento
      }, 
      (error) => {
        console.error("Erro no Firebase:", error);
        setLoading(false); // ERRO: TAMBÉM DESLIGA O CARREGAMENTO para a tela não travar
        if (error.code === 'failed-precondition') {
           // Este erro específico é o do Índice Criando
           console.warn("O índice ainda está sendo criado no Firebase...");
        }
      }
    );

    return () => unsubscribe();
  }, [user]);

  const filteredNotifications = useMemo(() => {
    if (filter === 'all') return notifications;
    return notifications.filter(n => n.type === filter);
  }, [notifications, filter]);

  const markAsRead = async (id) => {
    try {
      await updateDoc(doc(db, 'notifications', id), { read: true });
    } catch (e) { console.error(e); }
  };

  const deleteNotification = async (id) => {
    try {
      await deleteDoc(doc(db, 'notifications', id));
      toast.success("Aviso removido.");
    } catch (e) { toast.error("Erro ao remover."); }
  };

  const getIcon = (category) => {
    switch (category) {
      case 'order': return <Zap size={20} />;
      case 'chat': return <MessageSquare size={20} />;
      case 'support': return <Megaphone size={20} />;
      case 'punishment': return <Gavel size={20} />; // Ícone de Martelo para punições
      case 'reminder': return <Timer size={20} />;
      case 'review': return <Star size={20} />; // Para pedir avaliação
      case 'urgent': return <AlertTriangle size={20} />;
      default: return <Bell size={20} />;
    }
  };

  if (loading) return (
    <div className="h-screen flex flex-col items-center justify-center bg-white">
      <Loader2 className="animate-spin text-blue-600 mb-4" size={40} />
      <p className="font-black uppercase italic text-slate-400 text-[10px]">Sincronizando central...</p>
    </div>
  );

  return (
    <div className="relative min-h-screen font-sans text-slate-900 text-left bg-slate-50/30">
      <div className="fixed inset-0 -z-10 opacity-10" style={{ backgroundImage: `url(${planoFundo})`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
      <Navbar />

      <main className="max-w-5xl mx-auto px-6 pt-32 pb-20">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12 px-2 text-left">
          <div>
            <h1 className="text-4xl font-black uppercase italic tracking-tighter text-slate-900 leading-none">
              Central de <span className="text-blue-600">Avisos</span>
            </h1>
            <div className="flex gap-2 mt-6">
               <button onClick={() => setFilter('all')} className={`px-5 py-2 rounded-full text-[10px] font-black uppercase italic transition-all ${filter === 'all' ? 'bg-slate-900 text-white shadow-lg scale-105' : 'bg-white text-slate-400 border border-slate-100'}`}>Todos</button>
               <button onClick={() => setFilter('client')} className={`px-5 py-2 rounded-full text-[10px] font-black uppercase italic transition-all ${filter === 'client' ? 'bg-blue-600 text-white shadow-lg scale-105' : 'bg-white text-slate-400 border border-slate-100'}`}>Cliente</button>
               
               {/* SÓ MOSTRA FILTRO TÉCNICO SE O CARA FOR TÉCNICO */}
               {isTechUser && (
                <button onClick={() => setFilter('technician')} className={`px-5 py-2 rounded-full text-[10px] font-black uppercase italic transition-all ${filter === 'technician' ? 'bg-orange-600 text-white shadow-lg scale-105' : 'bg-white text-slate-400 border border-slate-100'}`}>Técnico</button>
               )}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {filteredNotifications.length > 0 ? (
            filteredNotifications.map((n) => (
              <div 
                key={n.id} 
                onClick={() => !n.read && markAsRead(n.id)}
                className={`group relative bg-white rounded-[2.5rem] border-none shadow-xl transition-all p-7 flex flex-col md:flex-row items-center gap-6 ${!n.read ? 'ring-2 ring-blue-600/5' : 'opacity-60'}`}
              >
                {/* Destaque lateral */}
                <div className={`absolute left-0 top-8 bottom-8 w-1.5 rounded-r-full ${n.type === 'technician' ? 'bg-orange-500' : 'bg-blue-600'}`} />
                
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${n.type === 'technician' ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'}`}>
                  {getIcon(n.category)}
                </div>

                <div className="flex-1 text-left">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className={`text-base leading-tight uppercase italic ${!n.read ? 'font-black text-slate-900' : 'font-bold text-slate-500'}`}>
                      {n.title}
                    </h3>
                    {!n.read && <Badge className={`${n.type === 'technician' ? 'bg-orange-600' : 'bg-blue-600'} text-[7px] font-black uppercase border-none shadow-sm`}>Novo</Badge>}
                  </div>
                  <p className="text-slate-500 text-sm font-bold italic mb-3 leading-relaxed">
                    {n.message}
                  </p>
                  
                  {/* DATA E HORA DETALHADAS */}
                  <div className="flex items-center gap-4 text-[9px] font-black text-slate-400 uppercase italic">
                    <div className="flex items-center gap-1.5">
                        <Calendar size={12} className="text-slate-300" />
                        {n.createdAt?.seconds ? new Date(n.createdAt.toDate()).toLocaleDateString('pt-BR') : 'Hoje'}
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Clock size={12} className="text-slate-300" />
                        {n.createdAt?.seconds ? new Date(n.createdAt.toDate()).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : 'Agora'}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 w-full md:w-auto">
                  {n.link && (
                    <Button 
                      onClick={(e) => { e.stopPropagation(); navigate(n.link); }}
                      className={`flex-1 md:flex-none h-12 px-6 rounded-2xl font-black uppercase text-[9px] italic ${n.type === 'technician' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-slate-900 hover:bg-slate-800'} text-white shadow-lg transition-all active:scale-95`}
                    >
                      Ver Detalhes
                    </Button>
                  )}
                  <button 
                    onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }} 
                    className="w-12 h-12 rounded-2xl flex items-center justify-center text-slate-200 hover:text-red-500 hover:bg-red-50 transition-all"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="py-24 bg-white rounded-[3.5rem] shadow-xl text-center flex flex-col items-center border-2 border-dashed border-slate-100">
              <BellOff size={48} className="text-slate-100 mb-4" />
              <p className="font-black uppercase italic text-slate-300 text-xs tracking-widest text-center">Nenhum aviso no momento</p>
            </div>
          )}
        </div>
      </main>
      <style dangerouslySetInnerHTML={{ __html: `.custom-scrollbar::-webkit-scrollbar { width: 5px; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }` }} />
    </div>
  );
};

export default Notification;